import React, { useEffect, useState } from 'react';
import { useSubscriptionStore } from '../../store/subscriptionStore';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import Modal from '../ui/Modal';
import { Button } from '../ui/Button';
import { SubscriptionPlan, SubscriptionTier, SubscriptionSource } from '../../types';
import { 
  X, 
  Crown, 
  TrendingUp, 
  Download, 
  HeadphonesIcon,
  Users,
  Star,
  Zap
} from 'lucide-react';

interface PricingModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultPlan?: string; // priceId to highlight
}

export const PricingModal: React.FC<PricingModalProps> = ({
  isOpen,
  onClose,
  defaultPlan
}) => {
  const {
    plans,
    subscription,
    isLoadingPlans,
    createCheckoutSession,
    updateSubscription,
    isCreatingSubscription,
    isUpdatingSubscription,
    loadPlans
  } = useSubscriptionStore();

  const { currentTier, hasActiveSubscription, isInTrial, isTrialExpired, needsSubscription } = useFeatureAccess();
  
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [billingInterval, setBillingInterval] = useState<'monthly' | 'yearly'>('yearly');

  useEffect(() => {
    if (isOpen && plans.length === 0) {
      loadPlans();
    }
  }, [isOpen, plans.length, loadPlans]);

  useEffect(() => {
    if (defaultPlan) {
      setSelectedPlan(defaultPlan);
      const plan = plans.find(p => p.priceId === defaultPlan);
      if (plan) {
        setBillingInterval(plan.interval === 'yearly' ? 'yearly' : 'monthly');
      }
    }
  }, [defaultPlan, plans]);

  const filteredPlans = plans.filter(plan => plan.interval === billingInterval);
  const basicPlans = filteredPlans.filter(plan => plan.tier === SubscriptionTier.Basic);
  const proPlans = filteredPlans.filter(plan => plan.tier === SubscriptionTier.Pro);

  const handleSubscribe = async (priceId: string) => {
    setSelectedPlan(priceId);
    
    try {
      // For active paid subscriptions (not trial), use the update flow
      if (hasActiveSubscription && !isTrialExpired && !needsSubscription && !isInTrial) {
        const success = await updateSubscription({ newPriceId: priceId });
        if (success) {
          onClose();
        }
      } else {
        // For trial users, new users, expired trials, or users who need a subscription, redirect to Stripe Checkout
        const { checkoutUrl } = await createCheckoutSession({ priceId });
        window.location.href = checkoutUrl;
      }
    } catch (error) {
      console.error('Subscription error:', error);
      setSelectedPlan(null);
    }
  };

  const isCurrentPlan = (tier: SubscriptionTier) => {
    // Only consider it the current plan if user has active PAID subscription (not trial)
    return currentTier === tier && hasActiveSubscription && !isTrialExpired && !needsSubscription && !isInTrial;
  };

  const getButtonText = (tier: SubscriptionTier) => {
    // Show "Coming Soon" for Pro plan
    if (tier === SubscriptionTier.Pro) {
      return 'Coming Soon';
    }
    
    if (isCurrentPlan(tier)) {
      return 'Current Plan';
    }
    
    // For expired or users who need subscription, all plans should be subscribable
    if (isTrialExpired || needsSubscription) {
      return 'Subscribe Now';
    }
    
    // For trial users, encourage them to subscribe
    if (isInTrial) {
      return 'Subscribe Now';
    }
    
    if (hasActiveSubscription) {
      return tier > currentTier ? 'Upgrade' : 'Downgrade';
    }
    
    return 'Start Free Trial';
  };

  const getButtonVariant = (tier: SubscriptionTier): 'primary' | 'outline' | 'ghost' => {
    if (isCurrentPlan(tier)) {
      return 'ghost';
    }
    
    // Use outline variant for all plans (Pro is disabled, Basic is standard)
    return 'outline';
  };

  const getPlanCard = (plans: SubscriptionPlan[], tier: SubscriptionTier, isPopular = false) => {
    const plan = plans[0];
    if (!plan) return null;

    const features = tier === SubscriptionTier.Basic 
      ? [
          { icon: <Users className="w-4 h-4" />, text: 'Unlimited habits' },
          { icon: <TrendingUp className="w-4 h-4" />, text: 'Progress tracking' },
          { icon: <Download className="w-4 h-4" />, text: 'Data export' },
          { icon: <HeadphonesIcon className="w-4 h-4" />, text: 'Email support' }
        ]
      : [
          { icon: <Users className="w-4 h-4" />, text: 'Everything in Basic' },
          { icon: <TrendingUp className="w-4 h-4" />, text: 'Advanced analytics' },
          { icon: <Star className="w-4 h-4" />, text: 'Habit insights' },
          { icon: <Zap className="w-4 h-4" />, text: 'Priority support' }
        ];

    const isLoading = (isCreatingSubscription || isUpdatingSubscription) && selectedPlan === plan.priceId;
    // Temporarily disable Pro plan while analytics are being developed
    const isProPlanDisabled = tier === SubscriptionTier.Pro;
    // Only disable buttons for actual current plans (paid subscriptions), not for trial users
    const isDisabled = isCurrentPlan(tier) || (isCreatingSubscription || isUpdatingSubscription) || isProPlanDisabled;

    return (
      <div
        key={plan.priceId}
        className={`relative p-6 rounded-lg border-2 transition-all ${
          tier === SubscriptionTier.Pro
            ? 'border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 opacity-75'
            : isPopular
            ? 'border-primary-500 ring-2 ring-primary-200 dark:ring-primary-800'
            : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
        } ${isCurrentPlan(tier) ? 'bg-gray-50 dark:bg-gray-800' : 'bg-white dark:bg-gray-900'}`}
      >
        {isPopular && (
          <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
            <span className="bg-primary-500 text-white px-3 py-1 text-xs font-semibold rounded-full">
              Most Popular
            </span>
          </div>
        )}

        {/* Show trial indicator for users currently in trial */}
        {isInTrial && currentTier === tier && (
          <div className="absolute -top-3 left-4">
            <span className="bg-blue-500 text-white px-3 py-1 text-xs font-semibold rounded-full">
              Trial
            </span>
          </div>
        )}

        <div className="text-center mb-6">
          <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            {plan.name.replace(' Monthly', '').replace(' Annual', '')}
          </h3>
          
          <div className="mb-4">
            <span className="text-3xl font-bold text-gray-900 dark:text-gray-100">
              ${plan.amount}
            </span>
            <span className="text-gray-600 dark:text-gray-400 ml-1">
              /{billingInterval === 'yearly' ? 'year' : 'month'}
            </span>
          </div>

          {plan.discountPercentage && billingInterval === 'yearly' && (
            <div className="inline-flex items-center px-2 py-1 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 text-sm rounded-full mb-4">
              Save {plan.discountPercentage}%
            </div>
          )}

          <p className="text-gray-600 dark:text-gray-400 text-sm">
            {plan.description}
          </p>
        </div>

        <ul className="space-y-3 mb-6">
          {features.map((feature, index) => (
            <li key={index} className="flex items-center space-x-3">
              <div className="text-green-500 flex-shrink-0">
                {feature.icon}
              </div>
              <span className="text-gray-700 dark:text-gray-300 text-sm">
                {feature.text}
              </span>
            </li>
          ))}
        </ul>

        <Button
          onClick={() => handleSubscribe(plan.priceId)}
          variant={getButtonVariant(tier)}
          className="w-full"
          disabled={isDisabled}
          isLoading={isLoading}
        >
          {getButtonText(tier)}
        </Button>

        {isCurrentPlan(tier) && (
          <p className="text-center text-xs text-gray-500 dark:text-gray-400 mt-2">
            You're currently on this plan
          </p>
        )}

        {/* Show coming soon notice for Pro plan */}
        {tier === SubscriptionTier.Pro && (
          <p className="text-center text-xs text-orange-600 dark:text-orange-400 mt-2">
            Advanced analytics features coming soon
          </p>
        )}

        {/* Show trial notice for users in trial */}
        {isInTrial && currentTier === tier && !isCurrentPlan(tier) && (
          <p className="text-center text-xs text-blue-600 dark:text-blue-400 mt-2">
            Currently in trial - subscribe to continue after trial ends
          </p>
        )}
      </div>
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              Choose Your Plan
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              {isInTrial 
                ? 'Subscribe now to secure your plan and continue after your trial'
                : hasActiveSubscription 
                ? 'Change your subscription plan'
                : 'Start building better habits today'
              }
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {subscription?.source === SubscriptionSource.Apple ? (
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-950 mt-4">
            <p className="text-sm text-blue-700 dark:text-blue-300 text-center">
              Your subscription is managed through the iOS App Store. To change your plan, open the Koru app on your iPhone.
            </p>
          </div>
        ) : (
          <>
            {/* Billing Toggle */}
            <div className="flex items-center justify-center mb-8">
              <div className="bg-gray-100 dark:bg-gray-800 p-1 rounded-lg">
                <button
                  onClick={() => setBillingInterval('monthly')}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                    billingInterval === 'monthly'
                      ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
                  }`}
                >
                  Monthly
                </button>
                <button
                  onClick={() => setBillingInterval('yearly')}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                    billingInterval === 'yearly'
                      ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
                  }`}
                >
                  Yearly
                  <span className="ml-1 text-xs text-green-600 dark:text-green-400">
                    Save 17%
                  </span>
                </button>
              </div>
            </div>

            {/* Loading State */}
            {isLoadingPlans && (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              </div>
            )}

            {/* Plans Grid */}
            {!isLoadingPlans && (
              <div className="grid md:grid-cols-2 gap-6">
                {getPlanCard(basicPlans, SubscriptionTier.Basic)}
                {getPlanCard(proPlans, SubscriptionTier.Pro, true)}
              </div>
            )}

            {/* Trial Notice */}
            {!hasActiveSubscription && !isInTrial && (
              <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-center">
                <Crown className="w-6 h-6 text-blue-600 dark:text-blue-400 mx-auto mb-2" />
                <p className="text-sm text-blue-800 dark:text-blue-200 font-medium">
                  All plans include a 7-day free trial
                </p>
                <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                  Cancel anytime during your trial at no charge
                </p>
              </div>
            )}

            {/* Footer */}
            <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-600">
              <p className="text-center text-xs text-gray-500 dark:text-gray-400">
                All plans can be cancelled anytime. No hidden fees.
              </p>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}; 